import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { bairroService } from '../services/bairroService';
import { Bairro } from '../types/bairro';

interface TerritoryDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  municipio: { id: number; nome: string; nome_original?: string; } | null;
  includedBairros: { id: number; nome_original: string; }[];
}

export const TerritoryDetailsModal: React.FC<TerritoryDetailsModalProps> = ({ isOpen, onClose, municipio, includedBairros }) => {
  const [allBairros, setAllBairros] = useState<Bairro[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !municipio) return;

    const fetchAllBairros = async () => {
      setLoading(true);
      try {
        const response = await bairroService.listar({
          municipio: municipio.nome,
          limit: 1000, // Assuming a large enough limit
        });
        setAllBairros(response.results);
      } catch (err) {
        console.error('Erro ao carregar todos os bairros:', err);
        toast.error('Erro ao carregar os bairros do território.');
      } finally {
        setLoading(false);
      }
    };

    fetchAllBairros();
  }, [isOpen, municipio]);

  if (!isOpen || !municipio) return null;

  const includedBairroIds = new Set(includedBairros.map(b => b.id));
  const excludedBairros = allBairros.filter(b => !includedBairroIds.has(b.id));

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
      <div className="relative mx-auto p-6 border shadow-lg rounded-md bg-white w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-start mb-4">
          <h2 className="text-2xl font-semibold text-gray-800">
            Detalhes de Atuação em {municipio.nome_original || municipio.nome}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800 text-2xl font-bold">&times;</button>
        </div>

        {loading ? (
          <div className="flex-grow flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            <p className="ml-4 text-gray-600">Carregando bairros...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 flex-grow overflow-y-auto pr-2">
            <div>
              <h3 className="text-lg font-semibold text-green-700 mb-3">
                Bairros Incluídos ({includedBairros.length})
              </h3>
              <ul className="space-y-1 h-full overflow-y-auto">
                {includedBairros.map(bairro => (
                  <li key={bairro.id} className="bg-green-50 p-2 rounded-md text-sm text-green-800">
                    {bairro.nome_original}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-red-700 mb-3">
                Bairros Não Incluídos ({excludedBairros.length})
              </h3>
              <ul className="space-y-1 h-full overflow-y-auto">
                {excludedBairros.map(bairro => (
                  <li key={bairro.id} className="bg-red-50 p-2 rounded-md text-sm text-red-800">
                    {bairro.nome_original}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <div className="flex justify-end items-center mt-6 pt-4 border-t">
          <button onClick={onClose} className="px-6 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};
